import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner, Alert, Row, Col, Form, Badge } from 'react-bootstrap';
import { colors } from '../brand-colors';

function MemeSession({ onComplete, onCancel, existingMeme }) {
  const [step, setStep] = useState(existingMeme ? 3 : 0); // 0=epimeme, 1=product memes, 2=campaign metaphors, 3=review
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Context data
  const [products, setProducts] = useState([]);
  const [ipFrameworks, setIpFrameworks] = useState([]);
  const [businessName, setBusinessName] = useState('');

  // Step 0: EpiMeme
  const [epimemeCandidates, setEpimemeCandidates] = useState([]);
  const [chosenEpimeme, setChosenEpimeme] = useState(null);
  const [showCustomEpimeme, setShowCustomEpimeme] = useState(false);
  const [customConcept, setCustomConcept] = useState('');
  const [customRationale, setCustomRationale] = useState('');

  // Step 1: Product Memes
  const [productMemes, setProductMemes] = useState([]);
  const [editingProductMeme, setEditingProductMeme] = useState(null);

  // Step 2: Campaign Metaphors
  const [campaignMetaphors, setCampaignMetaphors] = useState([]);
  const [editingMetaphor, setEditingMetaphor] = useState(null);

  // Load context on mount
  useEffect(() => {
    loadContext();
  }, []);

  // If existing meme data, pre-populate
  useEffect(() => {
    if (existingMeme) {
      if (existingMeme.epimeme) setChosenEpimeme(existingMeme.epimeme);
      if (existingMeme.product_memes) setProductMemes(existingMeme.product_memes);
      if (existingMeme.campaign_metaphors) setCampaignMetaphors(existingMeme.campaign_metaphors);
    }
  }, [existingMeme]);

  async function loadContext() {
    try {
      const res = await fetch('/api/brand-playbook/meme/context', { credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        setProducts(data.products || []);
        setIpFrameworks(data.ip_frameworks || []);
        setBusinessName(data.business_name || '');
      }
    } catch (e) {
      console.error('Failed to load meme context:', e);
    }
  }

  async function generateEpimemes() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/brand-playbook/meme/epimeme', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate');
      setEpimemeCandidates(data.candidates || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function generateProductMemes(epimemeOverride) {
    const epi = epimemeOverride || chosenEpimeme;
    if (!epi) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/brand-playbook/meme/product-memes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ epimeme: { concept: epi.concept, rationale: epi.rationale || '', examples: epi.examples || [] } }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate');
      setProductMemes(data.product_memes || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function generateCampaignMetaphors() {
    if (!chosenEpimeme || !productMemes.length) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/brand-playbook/meme/campaign-metaphors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          epimeme: { concept: chosenEpimeme.concept, rationale: chosenEpimeme.rationale || '', examples: chosenEpimeme.examples || [] },
          product_memes: productMemes.map(pm => ({ product_ids: pm.product_ids, product_name: pm.product_name, concept: pm.concept, rationale: pm.rationale || '', tagline_seeds: pm.tagline_seeds || [] })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate');
      setCampaignMetaphors(data.campaign_metaphors || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/brand-playbook/meme/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          epimeme: chosenEpimeme,
          product_memes: productMemes,
          campaign_metaphors: campaignMetaphors,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      onComplete({
        epimeme: chosenEpimeme,
        product_memes: productMemes,
        campaign_metaphors: campaignMetaphors,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // ---------- Step 0: EpiMeme ----------
  function renderEpimemeStep() {
    return (
      <>
        <div className="mb-4">
          <h5 className="mb-2">What is an EpiMeme?</h5>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Your EpiMeme is the <strong>company-wide master concept</strong> — a single unifying idea
            that ALL your marketing traces back to. It captures your worldview in a short, memorable phrase.
            Think "Just Do It" (Nike) or "Think Different" (Apple). Every campaign, product, and piece of
            content should reinforce this idea.
          </p>
        </div>

        {/* Show current epimeme if editing */}
        {chosenEpimeme && !epimemeCandidates.length && !loading && !showCustomEpimeme && (
          <div className="mb-4">
            <Card style={{ border: `2px solid ${colors.goldMid}`, backgroundColor: '#fffdf5' }}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <small className="text-muted text-uppercase" style={{ letterSpacing: '0.05em' }}>Current EpiMeme</small>
                    <h4 style={{ fontWeight: 600 }}>"{chosenEpimeme.concept}"</h4>
                    {chosenEpimeme.rationale && <p className="text-muted mb-0">{chosenEpimeme.rationale}</p>}
                  </div>
                  <Badge bg="success">Active</Badge>
                </div>
              </Card.Body>
            </Card>
            <div className="d-flex justify-content-between mt-3">
              <div className="d-flex gap-2">
                <Button variant="outline-dark" size="sm" onClick={generateEpimemes}>
                  Generate New Candidates
                </Button>
                <Button variant="outline-dark" size="sm" onClick={() => {
                  setShowCustomEpimeme(true);
                  setCustomConcept(chosenEpimeme.concept || '');
                  setCustomRationale(chosenEpimeme.rationale || '');
                }}>
                  Edit
                </Button>
              </div>
              <Button variant="dark" size="sm" onClick={() => setStep(productMemes.length ? 3 : 1)}>
                Continue →
              </Button>
            </div>
          </div>
        )}

        {/* No epimeme yet — fresh start */}
        {!chosenEpimeme && !epimemeCandidates.length && !loading && !showCustomEpimeme && (
          <div className="text-center py-4">
            <Button
              className="btn-brand-primary"
              onClick={generateEpimemes}
              disabled={loading}
            >
              Generate EpiMeme Candidates
            </Button>
            <div className="mt-3">
              <Button
                variant="link"
                size="sm"
                className="text-muted"
                onClick={() => setShowCustomEpimeme(true)}
              >
                I already have my EpiMeme
              </Button>
            </div>
          </div>
        )}

        {/* Custom EpiMeme form */}
        {showCustomEpimeme && (
          <Card className="mb-3" style={{ border: `2px solid ${colors.goldLight}`, backgroundColor: '#fffdf5' }}>
            <Card.Body>
              <h6 style={{ fontWeight: 600 }}>Enter Your EpiMeme</h6>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.85rem' }}>EpiMeme phrase (3-7 words)</Form.Label>
                <Form.Control
                  value={customConcept}
                  onChange={(e) => setCustomConcept(e.target.value)}
                  placeholder="e.g., Market Domination"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.85rem' }}>Why this works for your brand (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={customRationale}
                  onChange={(e) => setCustomRationale(e.target.value)}
                  placeholder="What worldview does this capture?"
                />
              </Form.Group>
              <div className="d-flex justify-content-between">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowCustomEpimeme(false)}
                >
                  Back
                </Button>
                <Button
                  className="btn-brand-primary"
                  disabled={!customConcept.trim()}
                  onClick={() => {
                    const custom = {
                      concept: customConcept.trim(),
                      rationale: customRationale.trim() || 'User-defined EpiMeme',
                      examples: [],
                    };
                    setChosenEpimeme(custom);
                    setShowCustomEpimeme(false);
                    setStep(1);
                    if (products.length) {
                      generateProductMemes(custom);
                    }
                  }}
                >
                  Use This EpiMeme
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {loading && !epimemeCandidates.length && (
          <div className="text-center py-4">
            <Spinner animation="border" style={{ color: colors.goldMid }} className="mb-2" />
            <p className="text-muted">Analyzing your brand DNA and generating candidates...</p>
          </div>
        )}

        {epimemeCandidates.length > 0 && (
          <>
            <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
              Select the EpiMeme that resonates most with your brand's worldview:
            </p>
            <Row>
              {epimemeCandidates.map((c, i) => (
                <Col md={6} key={i} className="mb-3">
                  <Card
                    className="h-100"
                    onClick={() => setChosenEpimeme(c)}
                    style={{
                      cursor: 'pointer',
                      border: chosenEpimeme === c ? `2px solid ${colors.goldLight}` : '1px solid #dee2e6',
                      backgroundColor: chosenEpimeme === c ? '#fffdf5' : '#fff',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Card.Body>
                      <h5 style={{ color: colors.greyDarkest, fontWeight: 600 }}>
                        "{c.concept}"
                      </h5>
                      <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                        {c.rationale}
                      </p>
                      {c.examples && c.examples.length > 0 && (
                        <div style={{ fontSize: '0.8rem' }}>
                          <strong>In action:</strong>
                          <ul className="mb-0 mt-1">
                            {c.examples.map((ex, j) => (
                              <li key={j} className="text-muted">{ex}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </Card.Body>
                    {chosenEpimeme === c && (
                      <Card.Footer style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, textAlign: 'center', fontSize: '0.85rem' }}>
                        Selected
                      </Card.Footer>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={generateEpimemes}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Generate New Options'}
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="text-muted"
                  onClick={() => setShowCustomEpimeme(true)}
                >
                  Write my own
                </Button>
              </div>
              <Button
                className="btn-brand-primary"
                onClick={() => {
                  setStep(1);
                  if (!productMemes.length && products.length) generateProductMemes();
                }}
                disabled={!chosenEpimeme || loading}
              >
                Continue with this EpiMeme
              </Button>
            </div>
          </>
        )}
      </>
    );
  }

  // ---------- Step 1: Product Memes ----------
  function renderProductMemesStep() {
    if (!products.length) {
      return (
        <Alert variant="warning">
          No products found. Add products in your Profit Map first, then come back to generate product memes.
          <div className="mt-2">
            <Button variant="outline-dark" size="sm" onClick={() => setStep(2)}>
              Skip to Campaign Metaphors
            </Button>
          </div>
        </Alert>
      );
    }

    return (
      <>
        <div className="mb-4">
          <h5 className="mb-2">Product Memes</h5>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Each product gets its own <strong>repeatable concept</strong> that reinforces your EpiMeme.
            This is the signature idea customers associate with this specific product — used in taglines,
            campaign hooks, and recurring references.
          </p>
          <div className="p-2 rounded mb-3" style={{ backgroundColor: '#f8f9fa', fontSize: '0.85rem' }}>
            <strong>EpiMeme:</strong> "{chosenEpimeme?.concept}"
          </div>
        </div>

        {loading && !productMemes.length && (
          <div className="text-center py-4">
            <Spinner animation="border" style={{ color: colors.goldMid }} className="mb-2" />
            <p className="text-muted">Generating product memes for {products.length} product{products.length > 1 ? 's' : ''}...</p>
          </div>
        )}

        {productMemes.length > 0 && (
          <>
            {productMemes.map((pm, i) => (
              <Card key={i} className="mb-3">
                <Card.Header style={{
                  backgroundColor: colors.greyDarkest,
                  color: colors.goldLight,
                  fontSize: '0.9rem',
                }}>
                  <span>{pm.product_name}</span>
                </Card.Header>
                <Card.Body>
                  {editingProductMeme === i ? (
                    <>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Product Meme Concept</Form.Label>
                        <Form.Control
                          value={pm.concept}
                          onChange={(e) => {
                            const updated = [...productMemes];
                            updated[i] = { ...updated[i], concept: e.target.value };
                            setProductMemes(updated);
                          }}
                          placeholder="Your product meme phrase (3-8 words)"
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Rationale (optional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={pm.rationale || ''}
                          onChange={(e) => {
                            const updated = [...productMemes];
                            updated[i] = { ...updated[i], rationale: e.target.value };
                            setProductMemes(updated);
                          }}
                          placeholder="Why this works and how it connects to the EpiMeme"
                        />
                      </Form.Group>
                      <Button size="sm" variant="outline-dark" onClick={() => setEditingProductMeme(null)}>Done</Button>
                    </>
                  ) : (
                    <>
                      <h5 style={{ fontWeight: 600 }}>"{pm.concept}"</h5>
                      <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>{pm.rationale}</p>
                      {pm.tagline_seeds && (
                        <div style={{ fontSize: '0.8rem' }}>
                          <strong>Tagline seeds:</strong>
                          <ul className="mb-0 mt-1">
                            {pm.tagline_seeds.map((t, j) => (
                              <li key={j} className="text-muted">{t}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 mt-2"
                        style={{ color: colors.goldDark, fontSize: '0.8rem' }}
                        onClick={() => setEditingProductMeme(i)}
                      >
                        Edit
                      </Button>
                    </>
                  )}
                </Card.Body>
              </Card>
            ))}
            <div className="d-flex justify-content-between mt-3">
              <Button variant="outline-secondary" size="sm" onClick={() => setStep(0)}>
                Back to EpiMeme
              </Button>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={generateProductMemes}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Regenerate All'}
                </Button>
                <Button
                  className="btn-brand-primary"
                  onClick={() => {
                    setStep(2);
                    if (!campaignMetaphors.length) generateCampaignMetaphors();
                  }}
                  disabled={loading}
                >
                  Continue
                </Button>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  // ---------- Step 2: Campaign Metaphors ----------
  function renderCampaignMetaphorsStep() {
    return (
      <>
        <div className="mb-4">
          <h5 className="mb-2">Campaign Metaphors</h5>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Each product gets a <strong>memorable mental anchor</strong> — an object, character, or experience
            that makes the abstract concept tangible. Think GEICO's gecko or Red Bull's wings.
            This becomes the recurring visual and verbal motif in campaigns.
          </p>
          <div className="p-2 rounded mb-3" style={{ backgroundColor: '#f8f9fa', fontSize: '0.85rem' }}>
            <strong>EpiMeme:</strong> "{chosenEpimeme?.concept}"
          </div>
        </div>

        {loading && !campaignMetaphors.length && (
          <div className="text-center py-4">
            <Spinner animation="border" style={{ color: colors.goldMid }} className="mb-2" />
            <p className="text-muted">Generating campaign metaphors...</p>
          </div>
        )}

        {campaignMetaphors.length > 0 && (
          <>
            {campaignMetaphors.map((cm, i) => (
              <Card key={i} className="mb-3">
                <Card.Header style={{
                  backgroundColor: colors.greyDarkest,
                  color: colors.goldLight,
                  fontSize: '0.9rem',
                }}>
                  {cm.product_name}
                </Card.Header>
                <Card.Body>
                  {editingMetaphor === i ? (
                    <>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Metaphor</Form.Label>
                        <Form.Control
                          value={cm.metaphor}
                          onChange={(e) => {
                            const updated = [...campaignMetaphors];
                            updated[i] = { ...updated[i], metaphor: e.target.value };
                            setCampaignMetaphors(updated);
                          }}
                          placeholder="The metaphor name or phrase"
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Description (optional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={cm.description || ''}
                          onChange={(e) => {
                            const updated = [...campaignMetaphors];
                            updated[i] = { ...updated[i], description: e.target.value };
                            setCampaignMetaphors(updated);
                          }}
                          placeholder="What it represents and how it connects to the product meme"
                        />
                      </Form.Group>
                      <Button size="sm" variant="outline-dark" onClick={() => setEditingMetaphor(null)}>Done</Button>
                    </>
                  ) : (
                    <>
                      <h5 style={{ fontWeight: 600 }}>{cm.metaphor}</h5>
                      <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>{cm.description}</p>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 mt-2"
                        style={{ color: colors.goldDark, fontSize: '0.8rem' }}
                        onClick={() => setEditingMetaphor(i)}
                      >
                        Edit
                      </Button>
                    </>
                  )}
                </Card.Body>
              </Card>
            ))}
            <div className="d-flex justify-content-between mt-3">
              <Button variant="outline-secondary" size="sm" onClick={() => setStep(1)}>
                Back to Product Memes
              </Button>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={generateCampaignMetaphors}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Regenerate All'}
                </Button>
                <Button
                  className="btn-brand-primary"
                  onClick={() => setStep(3)}
                  disabled={loading}
                >
                  Review & Save
                </Button>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  // ---------- Step 3: Review ----------
  function renderReviewStep() {
    return (
      <>
        <div className="mb-4">
          <h5 className="mb-2">Review Your Meme Hierarchy</h5>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            This is your complete meme hierarchy. Once saved, it feeds into Section 3 of your Playbook
            and is referenced by Campaign HQ and all content generation.
          </p>
        </div>

        {/* EpiMeme */}
        <Card className="mb-3">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, fontSize: '0.9rem' }}>
            EpiMeme (Master Concept)
          </Card.Header>
          <Card.Body>
            <h4 style={{ fontWeight: 600 }}>"{chosenEpimeme?.concept}"</h4>
            <p className="text-muted">{chosenEpimeme?.rationale}</p>
          </Card.Body>
        </Card>

        {/* Product Memes */}
        {productMemes.length > 0 && (
          <Card className="mb-3">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, fontSize: '0.9rem' }}>
              Product Memes
            </Card.Header>
            <Card.Body>
              {productMemes.map((pm, i) => (
                <div key={i} className={i < productMemes.length - 1 ? 'mb-3 pb-3 border-bottom' : ''}>
                  <strong>{pm.product_name}:</strong>{' '}
                  <span style={{ fontWeight: 600 }}>"{pm.concept}"</span>
                  <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.85rem' }}>{pm.rationale}</p>
                </div>
              ))}
            </Card.Body>
          </Card>
        )}

        {/* Campaign Metaphors */}
        {campaignMetaphors.length > 0 && (
          <Card className="mb-3">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, fontSize: '0.9rem' }}>
              Campaign Metaphors
            </Card.Header>
            <Card.Body>
              {campaignMetaphors.map((cm, i) => (
                <div key={i} className={i < campaignMetaphors.length - 1 ? 'mb-3 pb-3 border-bottom' : ''}>
                  <strong>{cm.product_name}:</strong>{' '}
                  <span style={{ fontWeight: 600 }}>{cm.metaphor}</span>
                  <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.85rem' }}>{cm.description}</p>
                </div>
              ))}
            </Card.Body>
          </Card>
        )}

        <div className="d-flex justify-content-between mt-4">
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" onClick={() => setStep(0)}>
              Edit EpiMeme
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={() => setStep(1)}>
              Edit Product Memes
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={() => setStep(2)}>
              Edit Campaign Metaphors
            </Button>
          </div>
          <Button
            className="btn-brand-primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? <><Spinner size="sm" className="me-2" />Saving...</> : 'Save Meme Hierarchy'}
          </Button>
        </div>
      </>
    );
  }

  const stepLabels = ['EpiMeme', 'Product Memes', 'Campaign Metaphors', 'Review'];

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header style={{
        backgroundColor: colors.greyDarkest,
        color: colors.goldLight,
        fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
        letterSpacing: '0.1em',
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <span>Meme Hierarchy — Creative Session</span>
          <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>
            Step {step + 1} of {stepLabels.length}: {stepLabels[step]}
          </span>
        </div>
      </Card.Header>
      <Card.Body className="p-4">
        {/* Progress */}
        <div className="d-flex gap-2 mb-4">
          {stepLabels.map((label, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                backgroundColor: i <= step ? colors.goldLight : '#e9ecef',
                transition: 'background-color 0.3s',
              }}
            />
          ))}
        </div>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

        {step === 0 && renderEpimemeStep()}
        {step === 1 && renderProductMemesStep()}
        {step === 2 && renderCampaignMetaphorsStep()}
        {step === 3 && renderReviewStep()}

        {/* Cancel link */}
        <div className="text-center mt-4">
          <Button
            variant="link"
            size="sm"
            className="text-muted"
            onClick={onCancel}
          >
            Cancel Session
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}

export default MemeSession;
